/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { emmanaged, emmanaged2, emmanaged3 } from '@/engine/PlanningAlgorithm/MatSDKHelpers';
import * as THREE from 'three';
import MatSDK from '../WasmModules/MatSDK';
import type * as MatSDKTypes from '../WasmModules/MatSDK';
import FileSaver from 'file-saver';

export function findNearestSegmentPoints(
  polyline: Array<THREE.Vector3>,
  points: Array<THREE.Vector3>
): Array<{ distance: number; segmentIndex: number; point: THREE.Vector3 }> {
  const vert: Array<number> = [];
  for (const pt of polyline) vert.push(pt.x, pt.y, pt.z);

  const seg: Array<number> = [];
  for (let i = 1; i < polyline.length; ++i) seg.push(i - 1, i);

  return emmanaged(new MatSDK.LineSegments().setFromRaw(vert, seg), (lines) =>
    emmanaged(MatSDK.buildEdgeTree(lines), (eTree) =>
      points.map((point) =>
        emmanaged(new MatSDK.Double3().set(point.x, point.y, point.z), (pt) => {
          const res = eTree.findNearestEdgePoint(lines, pt, 10000.0);
          const edgeIndex =
            res.edgeIndex != polyline.length - 2 && res.barycentric.y > 0.5 ? res.edgeIndex + 1 : res.edgeIndex;
          return {
            distance: res.distance,
            segmentIndex: edgeIndex,
            point: new THREE.Vector3().fromArray(res.point.toArray())
          };
        })
      )
    )
  );
}

export function findNearestSegmentPoint(
  polyline: Array<THREE.Vector3>,
  point: THREE.Vector3
): { distance: number; segmentIndex: number; point: THREE.Vector3 } {
  return findNearestSegmentPoints(polyline, [point])[0];
}

export function exportStl(surface: MatSDKTypes.TriangleSurface, name: string): void {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const buffer = surface.exportStl();
  FileSaver.saveAs(new Blob([buffer]), name);
}

export function ensureDefined<T>(value: T | undefined): T {
  if (value === undefined) {
    throw new Error('Value if undefined');
  }

  return value;
}

export function isPointInsideSpline2d(
  point: THREE.Vector3,
  splinePts: Array<THREE.Vector3>,
  to2dTransform: THREE.Matrix4
): boolean {
  const vert: Array<number> = [];
  for (const pt of splinePts) vert.push(pt.x, pt.y, pt.z);
  return emmanaged3(
    new MatSDK.Double2().setFromArray(point.clone().applyMatrix4(to2dTransform).toArray()),
    new MatSDK.Polyline3d().setFromRaw(vert, true),
    new MatSDK.MatrixDouble4x4().setFromArray(to2dTransform.toArray()),
    (point2d, polyline3d, matrix) =>
      emmanaged(polyline3d.transformed(matrix), (polyline3dNew) =>
        emmanaged(polyline3dNew.projectedOnXY(), (polyline2d) => polyline2d.isInside(point2d))
      )
  );
}

export function filterPointsInsideSpline2d(
  points: Array<THREE.Vector3>,
  splinePts: Array<THREE.Vector3>,
  to2dTransform: THREE.Matrix4
): Array<THREE.Vector3> {
  const vert: Array<number> = [];
  for (const pt of splinePts) vert.push(pt.x, pt.y, pt.z);
  return emmanaged2(
    new MatSDK.Polyline3d().setFromRaw(vert, true),
    new MatSDK.MatrixDouble4x4().setFromArray(to2dTransform.toArray()),
    (polyline3d, matrix) =>
      emmanaged(polyline3d.transformed(matrix), (polyline3dNew) =>
        emmanaged(polyline3dNew.projectedOnXY(), (polyline2d) => {
          const ret = new Array<THREE.Vector3>();
          for (const pt of points) {
            const isInside = emmanaged(
              new MatSDK.Double2().setFromArray(pt.clone().applyMatrix4(to2dTransform).toArray()),
              (point2d) => polyline2d.isInside(point2d)
            );
            if (isInside) {
              ret.push(pt);
            }
          }
          return ret;
        })
      )
  );
}
